import { createReducer, on } from '@ngrx/store';
import { ErrorModel } from '@spartacus/core';
import { Reference, ReferenceType } from '../../models/product-references';
import * as ProductReferencePricingActions from '../action/product-reference-pricing.action';
import { Options } from '../../models/product-info';
import { CustomVariantOption } from '../../../product-list-page/models/custom-product.model';

export interface ProductReferencePricingState {
  options: {
    SIMILAR?: Reference[];
    OTHERS?: Reference[];
  };
  selected: Options;
  variantType: string;
}

export const initialState: ProductReferencePricingState = {
  options: {},
  selected: null,
  variantType: null,
};

export const productReferencePricingReducer = createReducer(
  initialState,

  // Handle successful loading of product references
  on(ProductReferencePricingActions.loadProductsReferenceSuccess, (state, { options }) => {
    
    // Check if options is already in the correct object format
    if (options && typeof options === 'object' && !Array.isArray(options)) {
      // Options is already in the correct format
      return {
        ...state,
        options: options as { SIMILAR?: Reference[]; OTHERS?: Reference[] },
      };
    }
    
    // If options is an array, transform it to object structure
    if (Array.isArray(options)) {
      const transformedOptions = options.reduce((acc, reference) => {
        if (reference.referenceType === 'SIMILAR' || reference.referenceType === 'OTHERS') {
          if (!acc[reference.referenceType]) {
            acc[reference.referenceType] = [];
          }
          acc[reference.referenceType].push(reference);
        }
        return acc;
      }, {} as { SIMILAR?: Reference[]; OTHERS?: Reference[] });
      
      return {
        ...state,
        options: transformedOptions,
      };
    }
    
    // Fallback: return current state if options is neither array nor object
    return state;
  }),

  // Handle price loading for specific product codes
  on(ProductReferencePricingActions.loadProductsReferencePrice, (state, { productCodes }) => {

    const updatedOptions = { ...state.options };
    
    // Update SIMILAR references
    if (updatedOptions.SIMILAR) {
      updatedOptions.SIMILAR = updatedOptions.SIMILAR.map((reference) => ({
        ...reference,
        target: {
          ...reference.target,
          // Update baseOptions if they exist and have options
          ...(Array.isArray(reference.target?.baseOptions) && reference.target.baseOptions.length > 0 && {
            baseOptions: reference.target.baseOptions.map((baseOption) => ({
              ...baseOption,
              options: Array.isArray(baseOption.options)
                ? baseOption.options.map((option) =>
                    productCodes.includes(option.code)
                      ? { ...option, priceLoading: true, priceError: false }
                      : option
                  )
                : baseOption.options
            }))
          }),
          // Update variantOptions if baseOptions don't exist or are empty
          ...((
            !Array.isArray(reference.target?.baseOptions) || 
            reference.target.baseOptions.length === 0 ||
            !reference.target.baseOptions.some(bo => Array.isArray(bo.options) && bo.options.length > 0)
          ) && {
            variantOptions: Array.isArray(reference.target?.variantOptions)
              ? reference.target.variantOptions.map((variant) =>
                  productCodes.includes(variant.code)
                    ? { ...variant, priceLoading: true, priceError: false }
                    : variant
                )
              : reference.target?.variantOptions
          })
        },
      }));
    }

    // Update OTHERS references
    if (updatedOptions.OTHERS) {
      updatedOptions.OTHERS = updatedOptions.OTHERS.map((reference) => ({
        ...reference,
        target: {
          ...reference.target,
          // Update baseOptions if they exist and have options
          ...(Array.isArray(reference.target?.baseOptions) && reference.target.baseOptions.length > 0 && {
            baseOptions: reference.target.baseOptions.map((baseOption) => ({
              ...baseOption,
              options: Array.isArray(baseOption.options)
                ? baseOption.options.map((option) =>
                    productCodes.includes(option.code)
                      ? { ...option, priceLoading: true, priceError: false }
                      : option
                  )
                : baseOption.options
            }))
          }),
          // Update variantOptions if baseOptions don't exist or are empty
          ...((
            !Array.isArray(reference.target?.baseOptions) || 
            reference.target.baseOptions.length === 0 ||
            !reference.target.baseOptions.some(bo => Array.isArray(bo.options) && bo.options.length > 0)
          ) && {
            variantOptions: Array.isArray(reference.target?.variantOptions)
              ? reference.target.variantOptions.map((variant) =>
                  productCodes.includes(variant.code)
                    ? { ...variant, priceLoading: true, priceError: false }
                    : variant
                )
              : reference.target?.variantOptions
          })
        },
      }));
    }

    return {
      ...state,
      options: updatedOptions,
    };
  }),

  // Handle successful price loading for a specific product
  on(ProductReferencePricingActions.loadProductsReferencePriceSuccess, (state, { productCode, priceData, quotationFlag, obsoleteFlag }) => {
    
    const updatedOptions = { ...state.options };
    
    // Update SIMILAR references
    if (updatedOptions.SIMILAR) {
      updatedOptions.SIMILAR = updatedOptions.SIMILAR.map((reference) => ({
        ...reference,
        target: {
          ...reference.target,
          // Update baseOptions if they exist and have options
          ...(Array.isArray(reference.target?.baseOptions) && reference.target.baseOptions.length > 0 && {
            baseOptions: reference.target.baseOptions.map((baseOption) => ({
              ...baseOption,
              options: Array.isArray(baseOption.options)
                ? baseOption.options.map((option) =>
                    option.code === productCode
                      ? { 
                          ...option, 
                          priceLoading: false, 
                          priceError: false,
                          priceData,
                          quotationFlag,
                          obsoleteFlag
                        }
                      : option
                  )
                : baseOption.options
            }))
          }),
          // Update variantOptions if baseOptions don't exist or are empty
          ...((
            !Array.isArray(reference.target?.baseOptions) || 
            reference.target.baseOptions.length === 0 ||
            !reference.target.baseOptions.some(bo => Array.isArray(bo.options) && bo.options.length > 0)
          ) && {
            variantOptions: Array.isArray(reference.target?.variantOptions)
              ? reference.target.variantOptions.map((variant) =>
                  variant.code === productCode
                    ? { 
                        ...variant, 
                        priceLoading: false, 
                        priceError: false,
                        priceData,
                        quotationFlag,
                        obsoleteFlag
                      }
                    : variant
                )
              : reference.target?.variantOptions
          })
        },
      }));
    }

    // Update OTHERS references
    if (updatedOptions.OTHERS) {
      updatedOptions.OTHERS = updatedOptions.OTHERS.map((reference) => ({
        ...reference,
        target: {
          ...reference.target,
          // Update baseOptions if they exist and have options
          ...(Array.isArray(reference.target?.baseOptions) && reference.target.baseOptions.length > 0 && {
            baseOptions: reference.target.baseOptions.map((baseOption) => ({
              ...baseOption,
              options: Array.isArray(baseOption.options)
                ? baseOption.options.map((option) =>
                    option.code === productCode
                      ? { 
                          ...option, 
                          priceLoading: false, 
                          priceError: false,
                          priceData,
                          quotationFlag,
                          obsoleteFlag
                        }
                      : option
                  )
                : baseOption.options
            }))
          }),
          // Update variantOptions if baseOptions don't exist or are empty
          ...((
            !Array.isArray(reference.target?.baseOptions) || 
            reference.target.baseOptions.length === 0 ||
            !reference.target.baseOptions.some(bo => Array.isArray(bo.options) && bo.options.length > 0)
          ) && {
            variantOptions: Array.isArray(reference.target?.variantOptions)
              ? reference.target.variantOptions.map((variant) =>
                  variant.code === productCode
                    ? { 
                        ...variant, 
                        priceLoading: false, 
                        priceError: false,
                        priceData,
                        quotationFlag,
                        obsoleteFlag
                      }
                    : variant
                )
              : reference.target?.variantOptions
          })
        },
      }));
    }

    return {
      ...state,
      options: updatedOptions,
    };
  }),

  // Handle price loading failure for a specific product
  on(ProductReferencePricingActions.loadProductsReferencePriceFailure, (state, { productCode, error }) => {
    const updatedOptions = { ...state.options };
    
    // Update SIMILAR references
    if (updatedOptions.SIMILAR) {
      updatedOptions.SIMILAR = updatedOptions.SIMILAR.map((reference) => ({
        ...reference,
        target: {
          ...reference.target,
          // Update baseOptions if they exist and have options
          ...(Array.isArray(reference.target?.baseOptions) && reference.target.baseOptions.length > 0 && {
            baseOptions: reference.target.baseOptions.map((baseOption) => ({
              ...baseOption,
              options: Array.isArray(baseOption.options)
                ? baseOption.options.map((option) =>
                    option.code === productCode
                      ? { ...option, priceLoading: false, priceError: true, error: error }
                      : option
                  )
                : baseOption.options
            }))
          }),
          // Update variantOptions if baseOptions don't exist or are empty
          ...((
            !Array.isArray(reference.target?.baseOptions) || 
            reference.target.baseOptions.length === 0 ||
            !reference.target.baseOptions.some(bo => Array.isArray(bo.options) && bo.options.length > 0)
          ) && {
            variantOptions: Array.isArray(reference.target?.variantOptions)
              ? reference.target.variantOptions.map((variant) =>
                  variant.code === productCode
                    ? { ...variant, priceLoading: false, priceError: true, error: error }
                    : variant
                )
              : reference.target?.variantOptions
          })
        },
      }));
    }

    // Update OTHERS references
    if (updatedOptions.OTHERS) {
      updatedOptions.OTHERS = updatedOptions.OTHERS.map((reference) => ({
        ...reference,
        target: {
          ...reference.target,
          // Update baseOptions if they exist and have options
          ...(Array.isArray(reference.target?.baseOptions) && reference.target.baseOptions.length > 0 && {
            baseOptions: reference.target.baseOptions.map((baseOption) => ({
              ...baseOption,
              options: Array.isArray(baseOption.options)
                ? baseOption.options.map((option) =>
                    option.code === productCode
                      ? { ...option, priceLoading: false, priceError: true, error: error }
                      : option
                  )
                : baseOption.options
            }))
          }),
          // Update variantOptions if baseOptions don't exist or are empty
          ...((
            !Array.isArray(reference.target?.baseOptions) || 
            reference.target.baseOptions.length === 0 ||
            !reference.target.baseOptions.some(bo => Array.isArray(bo.options) && bo.options.length > 0)
          ) && {
            variantOptions: Array.isArray(reference.target?.variantOptions)
              ? reference.target.variantOptions.map((variant) =>
                  variant.code === productCode
                    ? { ...variant, priceLoading: false, priceError: true, error: error }
                    : variant
                )
              : reference.target?.variantOptions
          })
        },
      }));
    }

    return {
      ...state,
      options: updatedOptions,
    };
  })
);